import { ASSIGNMENT_BASE_OFFSET_REM, REM_TO_PX } from '../constants';
import { DayPortionDTO } from '../types/dto/response/planner-timeoff-response';

export const HEADER_OFFSET_PX = ASSIGNMENT_BASE_OFFSET_REM * REM_TO_PX;

interface TimeOffZone {
  topPx: number;
  heightPx: number;
}

interface AssignmentZone {
  topPx: number;
  heightPx: number;
}

export interface DayLayout {
  timeOff: TimeOffZone;
  assignment: AssignmentZone;
}

export function getPortionFraction(portion: DayPortionDTO): number {
  switch (portion) {
    case DayPortionDTO.FULL:
    case DayPortionDTO.HOURLY:
      return 1;
    case DayPortionDTO.HALF:
      return 0.5;
    case DayPortionDTO.QUARTER:
      return 0.25;
    default: {
      const _exhaustive: never = portion;
      throw new Error(`Unhandled DayPortionDTO: ${_exhaustive}`);
    }
  }
}

/**
 * Resolves the i18n key (under `timeOff.dayPart`) describing which part of the
 * day a time-off covers, so the tooltip text matches the UI:
 * - QUARTER → "First/Second/Third/Fourth quarter" by dayPartNumber (1-4)
 * - HALF → "First/Second half" by dayPartNumber (1-2)
 * - FULL → "Full day", HOURLY → "Hourly"
 */
export function getDayPartLabelKey(portion: DayPortionDTO, dayPartNumber: number | null): string {
  switch (portion) {
    case DayPortionDTO.FULL:
      return 'timeOff.dayPart.full';
    case DayPortionDTO.HOURLY:
      return 'timeOff.dayPart.hourly';
    case DayPortionDTO.HALF:
      return (dayPartNumber ?? 1) === 1
        ? 'timeOff.dayPart.firstHalf'
        : 'timeOff.dayPart.secondHalf';
    case DayPortionDTO.QUARTER: {
      const quarterKeys = [
        'timeOff.dayPart.firstQuarter',
        'timeOff.dayPart.secondQuarter',
        'timeOff.dayPart.thirdQuarter',
        'timeOff.dayPart.fourthQuarter',
      ];
      const index = Math.min(Math.max((dayPartNumber ?? 1) - 1, 0), quarterKeys.length - 1);
      return quarterKeys[index];
    }
    default: {
      const _exhaustive: never = portion;
      throw new Error(`Unhandled DayPortionDTO: ${_exhaustive}`);
    }
  }
}

/**
 * Vertical slot a non-hourly day occupies within the content area, as fractions
 * of the full content height: `offsetFraction` is the band's top, `fraction` its
 * height. QUARTER uses 25% bands by dayPartNumber (1-4), HALF uses 50% bands
 * (1-2), FULL/HOURLY fill the whole area. This is the SINGLE source of truth for
 * where a time-off block sits, shared by the block layout (time-off-day-stacking)
 * and the assignment free-zone (getDayLayout) so they always agree.
 */
export function getDayPartSlot(
  portion: DayPortionDTO,
  dayPartNumber: number | null
): { offsetFraction: number; fraction: number } {
  const fraction = getPortionFraction(portion);
  if (portion === DayPortionDTO.FULL || portion === DayPortionDTO.HOURLY) {
    return { offsetFraction: 0, fraction: 1 };
  }
  // HALF (parts 1-2) and QUARTER (parts 1-4): band index is (part - 1), clamped.
  const maxIndex = Math.round(1 / fraction) - 1; // HALF→1, QUARTER→3
  const index = Math.min(Math.max((dayPartNumber ?? 1) - 1, 0), maxIndex);
  return { offsetFraction: index * fraction, fraction };
}

/**
 * Computes non-overlapping vertical zones for time-off and assignment within the
 * content area of a row (below the utilization header). The time-off block sits
 * in its day-part slot (see {@link getDayPartSlot}); the assignment gets the
 * LARGER free gap left above or below the block, so a 2nd/3rd-quarter block (in
 * the middle) still leaves the assignment a clean contiguous band.
 *
 * @param cellHeight - Total row height in px
 * @param portion - Day portion type (FULL, HALF, QUARTER, HOURLY)
 * @param dayPartNumber - Which part of the day (1-based), null for FULL
 */
export function getDayLayout(
  cellHeight: number,
  portion: DayPortionDTO,
  dayPartNumber: number | null
): DayLayout {
  const contentHeight = cellHeight - HEADER_OFFSET_PX;
  const slot = getDayPartSlot(portion, dayPartNumber);

  const timeOffTop = HEADER_OFFSET_PX + contentHeight * slot.offsetFraction;
  const timeOffHeight = contentHeight * slot.fraction;

  // Free gaps above and below the block; the assignment takes the larger one.
  const gapAboveHeight = contentHeight * slot.offsetFraction;
  const gapBelowHeight = contentHeight * (1 - slot.offsetFraction - slot.fraction);

  const assignment =
    gapBelowHeight >= gapAboveHeight
      ? { topPx: timeOffTop + timeOffHeight, heightPx: gapBelowHeight }
      : { topPx: HEADER_OFFSET_PX, heightPx: gapAboveHeight };

  return {
    timeOff: { topPx: timeOffTop, heightPx: timeOffHeight },
    assignment,
  };
}
